import { BaseComponent } from '../core/BaseComponent';

export interface DatePickerOptions {
  format: string;
  selectedDate: Date | null;
  onSelect?: (date: Date) => void;
}

export class DatePicker extends BaseComponent<DatePickerOptions> {
  private input: HTMLInputElement | null = null;
  private calendar: HTMLElement | null = null;
  private currentDate: Date = new Date();
  private selectedDate: Date | null = null;

  protected getDefaultOptions(): DatePickerOptions {
    return {
      format: 'YYYY-MM-DD',
      selectedDate: null
    };
  }

  protected init(): void {
    super.init();
    this.input = this.element.querySelector('.date-picker-input');
    this.calendar = this.element.querySelector('.date-picker-calendar');
    
    if (this.input) {
      this.input.addEventListener('click', () => this.toggle());
      this.input.readOnly = true;
    }

    this.render();
    this.hide();
  }

  protected render(): void {
    if (!this.calendar) return;
    
    const year = this.currentDate.getFullYear();
    const monthName = new Intl.DateTimeFormat('en-US', { month: 'long' }).format(this.currentDate);
    
    this.calendar.innerHTML = `
      <div class="date-picker-header">
        <button class="date-picker-nav prev">&lt;</button>
        <div class="date-picker-title">${monthName} ${year}</div>
        <button class="date-picker-nav next">&gt;</button>
      </div>
      <div class="date-picker-grid">
        ${['S','M','T','W','T','F','S'].map(d => `<div class="date-picker-day-head">${d}</div>`).join('')}
        ${this.generateDays()}
      </div>
    `;

    this.calendar.querySelector('.prev')?.addEventListener('click', (e) => { e.stopPropagation(); this.changeMonth(-1); });
    this.calendar.querySelector('.next')?.addEventListener('click', (e) => { e.stopPropagation(); this.changeMonth(1); });
    
    this.calendar.querySelectorAll('.date-picker-day:not(.other-month)').forEach(day => {
      day.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectDate(parseInt(day.textContent || '0'));
      });
    });
  }

  private generateDays(): string {
    const year = this.currentDate.getFullYear();
    const month = this.currentDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const lastDate = new Date(year, month + 1, 0).getDate();
    const prevLastDate = new Date(year, month, 0).getDate();
    
    let days: string[] = [];
    
    for (let i = firstDay; i > 0; i--) {
      days.push(`<div class="date-picker-day other-month">${prevLastDate - i + 1}</div>`);
    }
    
    for (let i = 1; i <= lastDate; i++) {
        const d = new Date(year, month, i);
      const isToday = new Date().toDateString() === d.toDateString();
      const isSelected = this.selectedDate?.toDateString() === d.toDateString();
      days.push(`<div class="date-picker-day ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}">${i}</div>`);
    }
    
    return days.join('');
  }

  private changeMonth(delta: number): void {
    this.currentDate.setMonth(this.currentDate.getMonth() + delta);
    this.render();
  }

  public selectDate(day: number): void {
    this.selectedDate = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), day);
    if (this.input) {
      this.input.value = this.selectedDate.toISOString().split('T')[0];
    }
    this.render();
    this.hide();
    if (this.options.onSelect) this.options.onSelect(this.selectedDate);
    this.emit('select', this.selectedDate);
  }

  public toggle(): void {
    this.calendar?.classList.contains('d-none') ? this.show() : this.hide();
  }

  public show(): void {
    this.calendar?.classList.remove('d-none');
    const close = (e: MouseEvent) => {
      if (!this.element.contains(e.target as Node)) { this.hide(); document.removeEventListener('click', close as any); }
    };
    setTimeout(() => document.addEventListener('click', close as any), 0);
  }

  public hide(): void {
    this.calendar?.classList.add('d-none');
  }
}
